import React, { useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import BaseButton from "../Common/Buttons/BaseButton";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { leadDataStyles } from "../../../assets/MUIStyles/leadDataStyle";

const PatientTableFilters = ({ filtersObj }) => {
  const reducer = useSelector((reducer: any) => reducer);
  const {
    ProjectReducer: { leadsList },
  } = reducer;
  const {
    popoverOpen,
    handlePopoverClose,
    anchorEl,
    columnOrder,
    setFilteredData,
  } = filtersObj;
  const [filters, setFilters] = useState<
    { columnName: string; operator: string; value: string }[]
  >([{ columnName: "", operator: "contains", value: "" }]);

  const fetchSheetsData = (filters) => {
    if (!filters || filters?.length === 0) {
      setFilteredData(leadsList);
      return;
    }

    const newFiltered = leadsList.filter((item) =>
      filters.every((filter) => {
        const { columnName, operator, value } = filter;
        const itemValue = (item?.[columnName] || "").toString().toLowerCase();
        const filterValue = value.toLowerCase();

        switch (operator) {
          case "=":
            return itemValue === filterValue;
          case "!=":
            return itemValue !== filterValue;
          case "<":
            return (
              !isNaN(itemValue) &&
              !isNaN(filterValue) &&
              +itemValue < +filterValue
            );
          case ">":
            return (
              !isNaN(itemValue) &&
              !isNaN(filterValue) &&
              +itemValue > +filterValue
            );
          case "contains":
          default:
            return itemValue?.includes(filterValue);
        }
      })
    );

    setFilteredData(newFiltered);
  };

  const updateFilter = (index: number, e) => {
    const { name, value } = e.target;
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], [name]: value };
    setFilters(newFilters);
  };

  const addFilter = () => {
    const lastFilter = filters[0];

    if (!lastFilter.columnName || !lastFilter.operator || !lastFilter.value) {
      return;
    }

    setFilters([
      { columnName: "", operator: "contains", value: "" },
      ...filters,
    ]);
  };

  const removeFilter = (index: number) => {
    const updatedFilters = filters?.filter((_, i) => i !== index);
    setFilters(updatedFilters);
  };

  const applyFilters = async () => {
    await fetchSheetsData(filters);
  };

  const clearFilters = () => {
    setFilters([{ columnName: "", operator: "contains", value: "" }]);
    fetchSheetsData([]);
  };

  return (
    <Popover
      open={popoverOpen}
      anchorEl={anchorEl}
      onClose={handlePopoverClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          style: { padding: 16, width: 655, borderRadius: 8 },
        },
      }}
    >
      <Box
        className="flex-column"
        sx={{
          gap: 1,
        }}
      >
        {filters?.map((filter, index) => (
          <Stack
            direction="row"
            spacing={1}
            key={index}
            sx={{
              alignItems: "center",
            }}
          >
            <Select
              name="columnName"
              value={filter.columnName}
              onChange={(e) => updateFilter(index, e)}
              displayEmpty
              size="small"
              sx={leadDataStyles.select}
            >
              <MenuItem value="">
                <em>Select Column</em>
              </MenuItem>
              {columnOrder.map(({ key }, i) => (
                <MenuItem key={i} value={key as any}>
                  {key}
                </MenuItem>
              ))}
            </Select>

            <Select
              name="operator"
              value={filter.operator}
              onChange={(e) => updateFilter(index, e)}
              size="small"
              sx={leadDataStyles.select}
            >
              <MenuItem value="=">=</MenuItem>
              <MenuItem value="!=">!=</MenuItem>
              <MenuItem value="<">{"<"}</MenuItem>
              <MenuItem value=">">{">"}</MenuItem>
              <MenuItem value="contains">Contains</MenuItem>
            </Select>

            <TextField
              size="small"
              name="value"
              placeholder="Enter value"
              value={filter.value || ""}
              onChange={(e) => updateFilter(index, e)}
              sx={leadDataStyles.textField}
            />

            {index !== 0 && (
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => removeFilter(index)}
                sx={leadDataStyles.removeButton}
              >
                X
              </Button>
            )}

            {index === 0 && (
              <Button
                variant="outlined"
                onClick={addFilter}
                sx={commonStyles.clearButton}
                disabled={
                  !filter?.columnName || !filter?.operator || !filter?.value
                }
              >
                + Add
              </Button>
            )}
          </Stack>
        ))}

        <Box
          className="flex-end"
          sx={{
            mt: 2,
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                clearFilters();
                handlePopoverClose();
              }}
              sx={commonStyles.clearButton}
            >
              Clear Filters
            </Button>

            <BaseButton
              title="Apply Filters"
              variant="contained"
              handleClick={() => {
                applyFilters();
                handlePopoverClose();
              }}
            />
          </Stack>
        </Box>
      </Box>
    </Popover>
  );
};

export default PatientTableFilters;
